import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { differenceInWeeks, parseISO } from 'date-fns';
import { budgetService } from '../../services/endpoints/budgetService';
import {
  PEDCycle,
  PEDCycleDetail,
  CompoundType,
  PurchaseSettings,
} from '../../types';

// ── Math helpers ──────────────────────────────────────────────────────────────

function getCycleWeeks(cycle: PEDCycle): number {
  if (!cycle.endDate) return 0;
  const weeks = differenceInWeeks(parseISO(cycle.endDate), parseISO(cycle.startDate));
  return Math.max(weeks, 1);
}

function getDetailWeeks(detail: PEDCycleDetail, cycleWeeks: number): number {
  if (detail.startWeek != null && detail.endWeek != null) {
    return Math.max(detail.endWeek - detail.startWeek + 1, 1);
  }
  return cycleWeeks;
}

function calcVials(totalMg: number, concMgMl: number, vialMl: number): number {
  if (concMgMl <= 0 || vialMl <= 0) return 0;
  const mgPerVial = concMgMl * vialMl;
  return Math.ceil(totalMg / mgPerVial);
}

// ── Sub-component: one compound row ──────────────────────────────────────────

interface CompoundRowProps {
  detail: PEDCycleDetail;
  cycleWeeks: number;
  settings: PurchaseSettings;
  savedConc: number;
  savedVialSize: number;
  savedPrice: number;
  onPrefChange: (compoundId: string, conc: number, size: number, price: number) => void;
}

const CompoundRow: React.FC<CompoundRowProps> = ({
  detail, cycleWeeks, settings, savedConc, savedVialSize, savedPrice, onPrefChange,
}) => {
  const [conc, setConc]     = useState(savedConc || 250);
  const [size, setSize]     = useState(savedVialSize || 10);
  const [price, setPrice]   = useState(savedPrice || 0);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();

  // Sync when saved values load
  useEffect(() => { if (savedConc)  setConc(savedConc);  }, [savedConc]);
  useEffect(() => { if (savedVialSize) setSize(savedVialSize); }, [savedVialSize]);
  useEffect(() => { if (savedPrice) setPrice(savedPrice); }, [savedPrice]);

  const triggerSave = useCallback((c: number, s: number, p: number) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => onPrefChange(detail.compoundId, c, s, p), 800);
  }, [detail.compoundId, onPrefChange]);

  const weeksActive = getDetailWeeks(detail, cycleWeeks);
  const totalMg     = detail.weeklyTotal * weeksActive;
  const mgPerVial   = conc * size;
  const baseVials   = calcVials(totalMg, conc, size);
  const bufferedTotalMg = totalMg * (1 + settings.bufferPercent / 100);
  const bufferedVials   = calcVials(bufferedTotalMg, conc, size);
  const compoundCost    = bufferedVials * price;

  const name     = detail.compound?.name ?? 'Unknown Compound';
  const hasRange = detail.startWeek != null && detail.endWeek != null;

  return (
    <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-bold text-white text-base">{name}</h3>
          <p className="text-gray-400 text-sm">
            {hasRange
              ? `Weeks ${detail.startWeek}–${detail.endWeek}`
              : `Full cycle (${cycleWeeks} wks)`}
            {' · '}
            <span className="text-gold-400">{detail.weeklyTotal} mg/wk</span>
            {' · '}
            <span className="text-gray-300 font-semibold">{totalMg.toLocaleString()} mg total</span>
          </p>
        </div>
        {mgPerVial > 0 && (
          <span className="text-xs text-gray-500 bg-gray-700 px-2 py-1 rounded">
            {mgPerVial.toLocaleString()} mg/vial
          </span>
        )}
      </div>

      {/* Inputs */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Concentration (mg/ml)</label>
          <input
            type="number"
            min="1"
            value={conc}
            onChange={e => { const v = +e.target.value; setConc(v); triggerSave(v, size, price); }}
            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-sm text-white focus:border-gold-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Vial size (ml)</label>
          <input
            type="number"
            min="1"
            value={size}
            onChange={e => { const v = +e.target.value; setSize(v); triggerSave(conc, v, price); }}
            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-sm text-white focus:border-gold-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Price / vial ($)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={price}
            onChange={e => { const v = +e.target.value; setPrice(v); triggerSave(conc, size, v); }}
            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-sm text-white focus:border-gold-500 focus:outline-none"
          />
        </div>
      </div>

      {/* Results */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gray-700/60 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-white">{baseVials || '—'}</div>
          <div className="text-xs text-gray-400 mt-0.5">base vials</div>
          <div className="text-xs text-gray-500">minimum</div>
        </div>
        <div className="bg-gold-500/10 border border-gold-500/30 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-gold-400">{bufferedVials || '—'}</div>
          <div className="text-xs text-gray-300 mt-0.5">+{settings.bufferPercent}% buffer</div>
          <div className="text-xs text-gold-600 font-semibold">✓ buy this</div>
        </div>
        <div className="bg-gray-700/60 rounded-lg p-3 text-center">
          {price > 0 ? (
            <>
              <div className="text-2xl font-bold text-green-400">
                ${compoundCost.toFixed(2)}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">
                {bufferedVials} × ${price.toFixed(2)}
              </div>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-gray-500">—</div>
              <div className="text-xs text-gray-500 mt-0.5">enter price</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

interface PurchaseCalculatorProps {
  cycle: PEDCycle | null;
  allCycles: PEDCycle[];
  onCycleChange: (cycleId: string) => void;
}

const PurchaseCalculator: React.FC<PurchaseCalculatorProps> = ({
  cycle, allCycles, onCycleChange,
}) => {
  const queryClient = useQueryClient();
  const settingsTimer = useRef<ReturnType<typeof setTimeout>>();
  const [savedToShow, setSavedToShow] = useState(false);

  const { data: prefs, isLoading } = useQuery({
    queryKey: ['purchase-preferences'],
    queryFn: budgetService.getPreferences,
  });

  const [settings, setSettings] = useState<PurchaseSettings>({
    bufferPercent: 10,
    taxPercent: 0,
    shippingCost: 0,
  });

  // Sync settings from server once loaded
  useEffect(() => {
    if (prefs?.settings) setSettings(prefs.settings);
  }, [prefs?.settings]);

  const saveSettingsMutation = useMutation({
    mutationFn: budgetService.saveSettings,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['purchase-preferences'] }),
  });

  const saveCompoundMutation = useMutation({
    mutationFn: budgetService.saveCompoundPreference,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['purchase-preferences'] }),
  });

  const saveBudgetMutation = useMutation({
    mutationFn: ({ showId, item }: { showId: string; item: any }) =>
      budgetService.upsertBudgetItem(showId, item),
    onSuccess: (_, variables) => {
      setSavedToShow(true);
      setTimeout(() => setSavedToShow(false), 3000);
      queryClient.invalidateQueries({ queryKey: ['show-budget', variables.showId] });
    },
  });

  const handleSettingsChange = (updated: PurchaseSettings) => {
    setSettings(updated);
    clearTimeout(settingsTimer.current);
    settingsTimer.current = setTimeout(() => saveSettingsMutation.mutate(updated), 800);
  };

  const handlePrefChange = useCallback((
    compoundId: string, concentrationMgPerMl: number, vialSizeMl: number, pricePerVial: number
  ) => {
    saveCompoundMutation.mutate({ compoundId, concentrationMgPerMl, vialSizeMl, pricePerVial });
  }, [saveCompoundMutation]);

  // Filter to InjectableAAS details only
  const injectableDetails = (cycle?.details ?? []).filter(
    d => d.compound?.type === CompoundType.InjectableAAS
  );

  const cycleWeeks = cycle ? getCycleWeeks(cycle) : 0;

  // Build a map of saved preferences by compoundId
  const prefMap = Object.fromEntries(
    (prefs?.compoundPreferences ?? []).map(p => [p.compoundId, p])
  );

  // Calculate order totals
  const compoundTotals = injectableDetails.map(detail => {
    const saved = prefMap[detail.compoundId];
    const conc  = saved?.concentrationMgPerMl ?? 250;
    const size  = saved?.vialSizeMl ?? 10;
    const price = saved?.pricePerVial ?? 0;
    const weeksActive = getDetailWeeks(detail, cycleWeeks);
    const totalMg = detail.weeklyTotal * weeksActive;
    const bufferedTotalMg = totalMg * (1 + settings.bufferPercent / 100);
    const vials = calcVials(bufferedTotalMg, conc, size);
    return vials * price;
  });

  const subtotal  = compoundTotals.reduce((a, b) => a + b, 0);
  const taxAmount = subtotal * (settings.taxPercent / 100);
  const total     = subtotal + taxAmount + settings.shippingCost;
  const hasPrices = compoundTotals.some(c => c > 0);

  const handleSaveToShowBudget = () => {
    if (!cycle?.showId) return;
    const cycleName = cycle.name ?? 'Cycle';

    // Build per-compound vial breakdown for the budget item
    const vialBreakdown = injectableDetails.map(detail => {
      const saved  = prefMap[detail.compoundId];
      const conc   = saved?.concentrationMgPerMl ?? 250;
      const size   = saved?.vialSizeMl ?? 10;
      const price  = saved?.pricePerVial ?? 0;
      const weeks  = getDetailWeeks(detail, cycleWeeks);
      const totalMg = detail.weeklyTotal * weeks;
      const buffered = totalMg * (1 + settings.bufferPercent / 100);
      const vials  = calcVials(buffered, conc, size);
      return {
        compound:     detail.compound?.name     ?? 'Unknown',
        shortName:    detail.compound?.shortName ?? '',
        vials,
        pricePerVial: price,
        subtotal:     vials * price,
      };
    });

    saveBudgetMutation.mutate({
      showId: cycle.showId,
      item: {
        cycleId:     cycle.cycleId,
        category:    'DrugLoad',
        label:       `${cycleName} — drug load`,
        amount:      total,
        notes:       `${injectableDetails.length} compound(s), ${cycleWeeks} wks. Incl. ${settings.bufferPercent}% buffer, ${settings.taxPercent}% tax, $${settings.shippingCost} shipping.`,
        vialDetails: JSON.stringify(vialBreakdown),
      },
    });
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-500" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Cycle selector */}
      <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
        <label className="block text-sm font-semibold text-gray-300 mb-2">
          🛒 Select Cycle to Calculate
        </label>
        <select
          value={cycle?.cycleId ?? ''}
          onChange={e => onCycleChange(e.target.value)}
          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-gold-500 focus:outline-none"
        >
          <option value="">— Choose a cycle —</option>
          {allCycles.map(c => (
            <option key={c.cycleId} value={c.cycleId}>{c.name}</option>
          ))}
        </select>
      </div>

      {cycle && (
        <>
          {/* Order settings */}
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-semibold text-gray-300">Order Settings</h3>
              <span className="text-xs text-gray-500 italic">auto-saved</span>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Buffer (%)</label>
                <input
                  type="number" min="0" max="100"
                  value={settings.bufferPercent}
                  onChange={e => handleSettingsChange({ ...settings, bufferPercent: +e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-sm text-white focus:border-gold-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Tax (%)</label>
                <input
                  type="number" min="0" max="100" step="0.01"
                  value={settings.taxPercent}
                  onChange={e => handleSettingsChange({ ...settings, taxPercent: +e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-sm text-white focus:border-gold-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Shipping ($)</label>
                <input
                  type="number" min="0" step="0.01"
                  value={settings.shippingCost}
                  onChange={e => handleSettingsChange({ ...settings, shippingCost: +e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-sm text-white focus:border-gold-500 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Injectable compound cards */}
          {injectableDetails.length === 0 ? (
            <div className="bg-gray-800 rounded-xl p-8 border border-gray-700 text-center text-gray-500">
              No injectable compounds in this cycle.
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide">
                💉 Injectable Compounds
              </h3>
              {injectableDetails.map(detail => {
                const saved = prefMap[detail.compoundId];
                return (
                  <CompoundRow
                    key={detail.cycleDetailId}
                    detail={detail}
                    cycleWeeks={cycleWeeks}
                    settings={settings}
                    savedConc={saved?.concentrationMgPerMl ?? 0}
                    savedVialSize={saved?.vialSizeMl ?? 0}
                    savedPrice={saved?.pricePerVial ?? 0}
                    onPrefChange={handlePrefChange}
                  />
                );
              })}
            </div>
          )}

          {/* Order total */}
          <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
            <h3 className="text-sm font-semibold text-gray-300 mb-4">Order Estimate</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-400">
                <span>Subtotal</span>
                <span className={hasPrices ? 'text-white' : 'text-gray-600'}>
                  {hasPrices ? `$${subtotal.toFixed(2)}` : '—'}
                </span>
              </div>
              {settings.taxPercent > 0 && (
                <div className="flex justify-between text-gray-400">
                  <span>Tax ({settings.taxPercent}%)</span>
                  <span className={hasPrices ? 'text-white' : 'text-gray-600'}>
                    {hasPrices ? `$${taxAmount.toFixed(2)}` : '—'}
                  </span>
                </div>
              )}
              {settings.shippingCost > 0 && (
                <div className="flex justify-between text-gray-400">
                  <span>Shipping</span>
                  <span className="text-white">${settings.shippingCost.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t border-gray-600 pt-2 flex justify-between font-bold text-base">
                <span className="text-white">Total Estimate</span>
                <span className={hasPrices ? 'text-gold-400 text-lg' : 'text-gray-600'}>
                  {hasPrices ? `$${total.toFixed(2)}` : '—'}
                </span>
              </div>
            </div>

            {/* Save to Show Budget */}
            {cycle.showId && hasPrices && (
              <div className="mt-4">
                {savedToShow ? (
                  <div className="text-center text-green-400 text-sm font-semibold py-2">
                    ✓ Saved to Show Budget
                  </div>
                ) : (
                  <button
                    onClick={handleSaveToShowBudget}
                    disabled={saveBudgetMutation.isPending}
                    className="w-full bg-gold-500 hover:bg-gold-600 disabled:bg-gray-600 text-black font-bold py-2 px-4 rounded-lg transition-colors"
                  >
                    {saveBudgetMutation.isPending ? 'Saving…' : '💾 Save Estimate to Show Budget'}
                  </button>
                )}
              </div>
            )}
            {!cycle.showId && (
              <p className="mt-3 text-xs text-gray-600 text-center">
                Link this cycle to a show to save the estimate to the Show Budget.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PurchaseCalculator;
